// Second facteur (TOTP) — enrôlement, vérification, désactivation.
//
// Ce n'est pas un réglage parmi d'autres. La clé maîtresse est la racine de tout
// le chiffrement, et le mot de passe est ce qui l'ouvre : qui le vole ouvre le
// cabinet — les appareils n'y font pas obstacle, leur état est justement
// sauvegardé sous cette clé. **Le second facteur est le seul rempart devant le
// mot de passe.** L'écran le dit, plutôt que de le présenter comme une option.

import { useState, type FormEvent } from 'react';
import { useAuth } from './store';
import {
  disableTwoFactor,
  enableTwoFactor,
  groupSecret,
  qrSvg,
  setupTwoFactor,
  type TotpSetup,
} from './two-factor';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

const field =
  'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm ' +
  'focus:border-blue-600 focus:outline-none';

export function TwoFactor() {
  const session = useAuth((s) => s.session);
  const me = useAuth((s) => s.me);
  const setTwoFactor = useAuth((s) => s.setTwoFactor);

  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session || !me) return null;

  async function commencer() {
    setBusy(true);
    setError(null);
    try {
      setSetup(await setupTwoFactor(session!.sessionToken));
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function activer(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Le serveur exige un code valide : c'est ce qui prouve que l'application
      // d'authentification a bien enregistré le secret. Sans cette preuve, on
      // activerait une 2FA dont personne ne détient les codes — et le compte
      // serait perdu à la prochaine connexion.
      await enableTwoFactor(session!.sessionToken, code.trim());
      setTwoFactor(true);
      setSetup(null);
      setCode('');
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  async function desactiver(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await disableTwoFactor(session!.sessionToken, code.trim());
      setTwoFactor(false);
      setDisabling(false);
      setCode('');
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  // --- 2FA active ---------------------------------------------------------
  if (me.twoFactorEnabled) {
    return (
      <section
        className="mt-6 rounded border border-emerald-200 bg-emerald-50 p-4 text-left"
        data-testid="two-factor-on"
      >
        <h3 className="text-sm font-semibold text-emerald-900">
          Second facteur actif
        </h3>
        <p className="mt-1 text-xs text-emerald-800">
          Ton mot de passe ne suffit plus à entrer. C&apos;est le seul rempart qui
          tienne : il ouvre ta clé maîtresse, et donc tout le reste.
        </p>

        {!disabling ? (
          <button
            type="button"
            onClick={() => setDisabling(true)}
            data-testid="start-disable-2fa"
            className="mt-3 w-full text-xs text-slate-500 underline hover:text-slate-700"
          >
            Désactiver le second facteur
          </button>
        ) : (
          <form onSubmit={desactiver} className="mt-3">
            <p className="text-xs text-amber-800">
              Un code valide est exigé, même pour désactiver : un jeton de session
              volé ne doit pas suffire à baisser ta garde.
            </p>
            <label className="mt-2 block text-sm font-medium text-slate-700">
              Code à 6 chiffres
              <input
                className={field}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                data-testid="disable-2fa-code"
              />
            </label>
            {error && (
              <p data-testid="two-factor-error" className="mt-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy || code.trim().length < 6}
              data-testid="disable-2fa"
              className="mt-3 w-full rounded border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              {busy ? 'Vérification…' : 'Confirmer la désactivation'}
            </button>
          </form>
        )}
      </section>
    );
  }

  // --- Enrôlement en cours : le QR ----------------------------------------
  if (setup) {
    return (
      <section
        className="mt-6 rounded border border-blue-200 bg-blue-50 p-4 text-left"
        data-testid="two-factor-setup"
      >
        <h3 className="text-sm font-semibold text-blue-900">
          Scanne ce code avec ton application d&apos;authentification
        </h3>

        {/* Le QR est fabriqué DANS la page : le secret ne sort pas d'ici. */}
        <div
          data-testid="totp-qr"
          className="mx-auto mt-3 w-48 rounded bg-white p-2 [&_svg]:h-auto [&_svg]:w-full"
          dangerouslySetInnerHTML={{ __html: qrSvg(setup.otpauthUri) }}
        />

        <p className="mt-3 text-xs text-blue-800">
          Si tu ne peux pas scanner, saisis ce secret à la main :
        </p>
        <p
          data-testid="totp-secret"
          className="mt-1 select-all break-all rounded bg-white px-2 py-1 text-center font-mono text-xs tracking-wider text-slate-800"
        >
          {groupSecret(setup.secret)}
        </p>

        <form onSubmit={activer} className="mt-4">
          <label className="block text-sm font-medium text-slate-700">
            Puis recopie le code affiché
            <input
              className={field}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              data-testid="enable-2fa-code"
              autoFocus
            />
          </label>

          {error && (
            <p data-testid="two-factor-error" className="mt-2 text-sm text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || code.trim().length < 6}
            data-testid="enable-2fa"
            className="mt-3 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {busy ? 'Vérification…' : 'Activer le second facteur'}
          </button>
        </form>

        <p className="mt-3 text-[11px] text-blue-700">
          Rien n&apos;est activé tant que ce code n&apos;est pas vérifié : on ne
          te verrouillera pas dehors avec une 2FA dont tu n&apos;aurais pas les
          codes.
        </p>
        <p className="mt-2 text-[11px] text-blue-700">
          Si tu perds ton téléphone, tes <strong>dépositaires</strong> restent la
          porte de sortie : une récupération lève le second facteur.
        </p>
      </section>
    );
  }

  // --- Pas de 2FA ---------------------------------------------------------
  return (
    <section
      className="mt-6 rounded border border-amber-200 bg-amber-50 p-4 text-left"
      data-testid="two-factor-off"
    >
      <h3 className="text-sm font-semibold text-amber-900">
        Ton mot de passe est la seule porte
      </h3>
      <p className="mt-1 text-xs text-amber-800">
        Il ouvre ta clé maîtresse — et la clé maîtresse ouvre tout : tes dossiers,
        tes documents, et jusqu&apos;à l&apos;identité de tes appareils, dont
        l&apos;état est sauvegardé sous elle.{' '}
        <strong>Un appareil n&apos;est donc pas un second facteur.</strong> Celui-ci
        en est un.
      </p>

      {error && (
        <p data-testid="two-factor-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={commencer}
        disabled={busy}
        data-testid="setup-2fa"
        className="mt-3 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
      >
        {busy ? '…' : 'Activer le second facteur'}
      </button>
    </section>
  );
}
